import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(() => localStorage.getItem('auth_token'));

    const logout = useCallback(() => {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
    }, []);

    // Validate token and fetch user on mount / token change
    useEffect(() => {
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }

        localStorage.setItem('auth_token', token);

        authAPI.me(token)
            .then(res => {
                setUser(res.data.user);
            })
            .catch(() => {
                logout();
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token, logout]);

    const saveToken = useCallback((newToken) => {
        setToken(newToken);
    }, []);

    const isAuthenticated = !!user && !!token;
    const isAdmin = isAuthenticated && user?.role === 'admin';
    const isApproved = isAuthenticated && user?.status === 'approved';
    const isPending = isAuthenticated && user?.status === 'pending';
    const isBlocked = isAuthenticated && user?.status === 'blocked';

    return (
        <AuthContext.Provider value={{
            user, token, loading, isAuthenticated,
            isAdmin, isApproved, isPending, isBlocked,
            saveToken, logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
